import React from 'react';
import styled from 'styled-components';
import { CreditCard, Clock, CheckCircle, Wallet, Users, Info } from 'lucide-react';
import { SpecialPaymentsSummary } from '../../../services/dashboardService';
import { formatCurrency } from '../../../services/specialPaymentsService';

interface Props {
  data: SpecialPaymentsSummary;
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color: string; $noBackground?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$noBackground ? 'transparent' : `color-mix(in srgb, ${props.$color} 8%, transparent)`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  direction: ltr;
  text-align: right;
  margin-bottom: 4px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
`;

const InfoNote = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  color: var(--text-secondary);
  padding: 14px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
`;

const InfoIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
  filter: saturate(0.7) brightness(0.9);
`;

const AdminSpecialPaymentsWidget: React.FC<Props> = ({ data }) => {
  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <CreditCard size={20} />
        </SectionIcon>
        <SectionTitle>תשלומים מיוחדים</SectionTitle>
      </SectionHeader>

      <CardsGrid>
        <Card $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <Clock size={20} />
            </CardIcon>
            <CardTitle>תשלומים פעילים (ממתינים)</CardTitle>
          </CardHeader>
          <CardValue>{data.activePaymentsCount}</CardValue>
          <CardSubValue>
            סכום כולל: {formatCurrency(data.activePaymentsAmount)}
          </CardSubValue>
        </Card>

        <Card $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <CheckCircle size={20} />
            </CardIcon>
            <CardTitle>שולמו החודש</CardTitle>
          </CardHeader>
          <CardValue>{data.paidThisMonthCount}</CardValue>
          <CardSubValue>
            סכום: {formatCurrency(data.paidThisMonthAmount)}
          </CardSubValue>
        </Card>

        <Card $accentColor="var(--primary-color)">
          <CardHeader>
            <CardIcon $color="var(--primary-color)">
              <Wallet size={20} />
            </CardIcon>
            <CardTitle>סה"כ נגבה (היסטורי)</CardTitle>
          </CardHeader>
          <CardValue style={{ fontSize: '22px' }}>
            {formatCurrency(data.totalEverPaid)}
          </CardValue>
          <CardSubValue>מתחילת הפעילות</CardSubValue>
        </Card>

        <Card $accentColor="#6366f1">
          <CardHeader>
            <CardIcon $color="#6366f1" $noBackground>
              <Users size={20} />
            </CardIcon>
            <CardTitle>לקוחות עם תשלומים</CardTitle>
          </CardHeader>
          <CardValue>{data.uniqueClientsCount}</CardValue>
          <CardSubValue>לקוחות ייחודיים</CardSubValue>
        </Card>
      </CardsGrid>

      <InfoNote>
        <InfoIcon>
          <Info size={16} />
        </InfoIcon>
        ניהול נפרד של תשלומים מיוחדים - לא קשור לתשלומי עמלות רגילים
      </InfoNote>
    </Container>
  );
};

export default AdminSpecialPaymentsWidget;
